import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Table, Tag, Typography, message } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { clientService, type ClientInfo, type ClientCreateResult } from "@/services/client";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

const { Text, Paragraph } = Typography;

export function ClientManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const canManage = hasPermission("OrgRoleManage");

  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientInfo | null>(null);
  const [newApiKey, setNewApiKey] = useState("");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try { setClients(await clientService.list(orgId)); }
    catch { setClients([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  useCardExtra(
    canManage ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
        {t("clients.create")}
      </Button>
    ) : null,
    [t, permissions],
  );

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const result = await clientService.create(orgId, newName.trim());
      setNewName("");
      setCreateOpen(false);
      setNewApiKey(result.api_key);
      setKeyModalOpen(true);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
    finally { setSaving(false); }
  };

  const openEdit = (client: ClientInfo) => {
    setEditingClient(client);
    editForm.setFieldsValue({ name: client.name });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editingClient) return;
    const values = await editForm.validateFields();
    setSaving(true);
    try {
      await clientService.update(orgId, editingClient.id, values.name);
      setEditOpen(false);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await clientService.delete(orgId, id);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const handleRegenerateKey = async (clientId: string) => {
    try {
      const result = await clientService.regenerateKey(orgId, clientId);
      setNewApiKey(result.api_key);
      setKeyModalOpen(true);
      message.success(t("common:success"));
    } catch { message.error(t("common:error")); }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
  };

  const columns = [
    { title: t("clients.table.name"), dataIndex: "name", key: "name" },
    {
      title: t("clients.table.status"), dataIndex: "status", key: "status",
      render: (s: string) => (
        <Tag color={s === "online" ? "green" : "default"}>
          {s === "online" ? "Online" : "Offline"}
        </Tag>
      ),
    },
    {
      title: t("clients.table.capabilities"), dataIndex: "capabilities", key: "capabilities",
      render: (caps: string[]) => caps.length > 0 ? caps.join(", ") : "—",
    },
    {
      title: t("clients.table.last_heartbeat"), dataIndex: "last_heartbeat", key: "last_heartbeat",
      render: (v: string | null) => formatDateTime(v),
    },
    ...(canManage ? [{
      title: t("common:action"), key: "action", width: 140,
      render: (_: unknown, record: ClientInfo) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("clients.regenerate_key.confirm")} onConfirm={() => handleRegenerateKey(record.id)}>
            <Button type="text" icon={<KeyOutlined />} size="small" />
          </Popconfirm>
          <Popconfirm title={t("clients.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    }] : []),
  ];

  return (
    <>
      <Table dataSource={clients} columns={columns} rowKey="id" loading={loading} size="middle" />

      {/* Create Client Modal */}
      <Modal title={t("clients.create")} open={createOpen} onOk={handleCreate} onCancel={() => setCreateOpen(false)} confirmLoading={saving}>
        <Input
          placeholder={t("clients.table.name")}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
        />
      </Modal>

      {/* Edit Client Modal */}
      <Modal title={t("common:edit")} open={editOpen} onOk={handleEdit} onCancel={() => setEditOpen(false)} confirmLoading={saving}>
        <Form form={editForm} layout="vertical">
          <Form.Item label={t("clients.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* API Key Display Modal */}
      <Modal
        title="API Key"
        open={keyModalOpen}
        onCancel={() => { setKeyModalOpen(false); setNewApiKey(""); }}
        footer={<Button onClick={() => { setKeyModalOpen(false); setNewApiKey(""); }}>OK</Button>}
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="warning">{t("clients.key_warning")}</Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--content-bg)", padding: "8px 12px", borderRadius: 6 }}>
          <Paragraph copyable style={{ margin: 0, flex: 1, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
            {newApiKey}
          </Paragraph>
        </div>
      </Modal>
    </>
  );
}
